import { SuscriptionRepositoryImplementation } from '../infrastructure/suscriptions-repository-implementation';
import { Subscription, Plan } from '../domain/suscriptions/model'; // Assuming Plan is also defined in model.ts
import { firstValueFrom } from 'rxjs';

// Initialize repository. In a real app, uid and cid would likely come from context or auth.
// For now, using placeholders.
import { cacheManager } from '../infrastructure/cache-manager';

const subscriptionRepo = new SuscriptionRepositoryImplementation();

const PLANS_PREFIX = 'subscription_plans';
const SUBSCRIPTIONS_PREFIX = 'subscriptions';

async function getPlans(): Promise<any> {
  try {
    const cacheKey = `${PLANS_PREFIX}-list`;
    const cached = cacheManager.getItem<any>(cacheKey);

    if (cached) {
      console.log('Returning plans from cache.');
      return cached;
    }

    const result = await firstValueFrom(subscriptionRepo.getPlans());
    if (result.success && result.data.total > 0) {
      cacheManager.setItem(cacheKey, result.data.data);
      return result.data.data;
    }
    return [];
  } catch (error) {
    console.error('Error fetching plans:', error);
    throw error;
  }
}

async function getPlan(id: number): Promise<Plan> {
  try {
    const cacheKey = `${PLANS_PREFIX}-detail-${id}`;
    const cached = cacheManager.getItem<Plan>(cacheKey);

    if (cached) {
      console.log(`Returning plan with ID ${id} from cache.`);
      return cached;
    }

    const result = await firstValueFrom(subscriptionRepo.getPlan(id));
    if (result) {
      cacheManager.setItem(cacheKey, result);
      return result;
    }
    throw new Error(`Plan with ID ${id} not found.`);
  } catch (error) {
    console.error(`Error fetching plan with ID ${id}:`, error);
    throw error;
  }
}

async function createSubscription(subscriptionData: Partial<Subscription>): Promise<Subscription> {
  try {
    const result = await firstValueFrom(
      subscriptionRepo.createSubscription(subscriptionData)
    );

    // Invalidate caches after creation
    cacheManager.invalidate(PLANS_PREFIX);
    cacheManager.invalidate(SUBSCRIPTIONS_PREFIX);

    return result;
  } catch (error) {
    console.error('Error creating subscription:', error);
    throw error;
  }
}

async function getSubscription(): Promise<Subscription> {
  // Assuming getSubscription returns the CURRENT subscription and doesn't take an ID?
  // The original code was: await subscriptionRepo.getSubscription().toPromise();
  // If it's the current user's subscription, maybe we cache it?
  // Let's cache it under subscriptions prefix.
  const cacheKey = `${SUBSCRIPTIONS_PREFIX}-current`;
  const cached = cacheManager.getItem<Subscription>(cacheKey);

  if (cached) {
    return cached;
  }

  const result = await subscriptionRepo.getSubscription().toPromise();
  if (result) {
    cacheManager.setItem(cacheKey, result);
    return result
  }
  throw new Error(`Subscription with ID not found.`);
}

async function getSubscriptions(page: number, limit: number): Promise<Subscription[]> {
  try {
    const cacheKey = `${SUBSCRIPTIONS_PREFIX}-list-${page}-${limit}`;
    const cached = cacheManager.getItem<Subscription[]>(cacheKey);

    if (cached) {
      console.log(`Returning subscriptions for page ${page}, limit ${limit} from cache.`);
      return cached;
    }

    const result = await firstValueFrom(subscriptionRepo.getSubscriptions(page, limit));
    if (result && result.length > 0) {
      cacheManager.setItem(cacheKey, result);
      return result;
    }
    return [];
  } catch (error) {
    console.error(`Error fetching subscriptions (page: ${page}, limit: ${limit}):`, error);
    throw error;
  }
}

async function updateSubscription(id: number, subscriptionData: Partial<Subscription>): Promise<Subscription> {
  try {
    const result = await firstValueFrom(
      subscriptionRepo.updateSubscription(id, subscriptionData)
    );

    // Invalidate caches after update
    cacheManager.invalidate(PLANS_PREFIX);
    cacheManager.invalidate(SUBSCRIPTIONS_PREFIX);

    return result;
  } catch (error) {
    console.error(`Error updating subscription with ID ${id}:`, error);
    throw error;
  }
}

async function cancelSubscription(id: number): Promise<void> {
  try {
    await firstValueFrom(subscriptionRepo.cancelSubscription(id));
    // Invalidate caches after cancellation
    cacheManager.invalidate(PLANS_PREFIX);
    cacheManager.invalidate(SUBSCRIPTIONS_PREFIX);
  } catch (error) {
    console.error(`Error canceling subscription with ID ${id}:`, error);
    throw error;
  }
}

async function associateSubscription(data: any): Promise<any> {
  try {
    const result = await firstValueFrom(subscriptionRepo.associateSubscription(data));
    // Invalidate caches
    cacheManager.invalidate(PLANS_PREFIX);
    cacheManager.invalidate(SUBSCRIPTIONS_PREFIX);
    return result;
  } catch (error) {
    console.error('Error associating subscription:', error);
    throw error;
  }
}

async function upgradeSubscription(planId: number): Promise<any> {
  try {
    const result = await firstValueFrom(subscriptionRepo.upgradeSubscription(planId));
    // Invalidate caches
    cacheManager.invalidate(PLANS_PREFIX);
    cacheManager.invalidate(SUBSCRIPTIONS_PREFIX);
    return result;
  } catch (error) {
    console.error('Error upgrading subscription:', error);
    throw error;
  }
}

export {
  getPlans,
  getPlan,
  createSubscription,
  getSubscription,
  getSubscriptions,
  updateSubscription,
  cancelSubscription,
  associateSubscription,
  upgradeSubscription,
};
